import React, { useState } from 'react';
import classNames from 'classnames';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/styles';
import { Spin } from 'antd';
import { CheckOutlined } from '@ant-design/icons';

import { QUERY_ALL_ARTICLES } from '../queries';

const useStyles = makeStyles(theme => ({
  container: {
    borderRadius: 2,
    border: '1px solid #D9D9D9',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: 48,
    padding: '0 20px',
    backgroundColor: theme.colors.blue1,
  },
  headerLeft: {
    flex: 1,
    width: 'calc(100% - 40px)',
    display: 'flex',
  },
  selectedLabel: {
    ...theme.typography.body2,
    color: theme.colors.captionColor,
  },
  selectedVal: {
    flex: 1,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    ...theme.typography.h5,
    color: theme.colors.titleColor,
    paddingLeft: 4,
  },
  clearText: {
    ...theme.typography.body2,
    color: theme.colors.mainGreen6,
    cursor: 'pointer',
  },
  emptyText: {
    ...theme.typography.body2,
    color: theme.colors.blue6,
  },
  sectionHeader: {
    height: 32,
    lineHeight: '32px',
    paddingLeft: 20,
    marginBottom: 0,
    color: theme.colors.captionColor,
    backgroundColor: theme.colors.gray2,
  },
  article: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '13px 20px 13px 32px',
    cursor: 'pointer',

    '&:hover': {
      backgroundColor: theme.colors.gray2,
    },

    '& > div': {
      flex: 1,
      display: 'flex',

      '& > span:last-child': {
        flex: 1,
        paddingRight: 8,
        wordBreak: 'break-word',
      },
    },

    '& > span': {
      color: theme.colors.mainGreen6,
    },
  },
  sectionList: {
    maxHeight: 376,
    overflowY: 'auto',
  },
  selectedArticle: {
    backgroundColor: `${theme.colors.mainGreen1} !important`,
  },
  articleNum: {
    ...theme.typography.h5,
    color: theme.colors.disableColor,
  },
  articleTitle: {
    ...theme.typography.body2,
    color: theme.colors.titleColor,
    paddingLeft: 27,
  },
  sectionEmpty: {
    padding: '13px 32px',
    ...theme.typography.body2,
    color: theme.colors.disableColor,
  },
}));

const Articles = ({ value, onChange }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [selectedTitle, setSelectedTitle] = useState(null);

  const { data, loading } = useQuery(QUERY_ALL_ARTICLES, {
    fetchPolicy: 'network-only',
  });

  const { articles } = data ?? {};

  const handleSelectArticle = ({ id, title }) => {
    onChange(id);
    setSelectedTitle(title);
  };

  const handleClearClick = () => {
    onChange(null);
    setSelectedTitle('');
  };

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <Choose>
          <When condition={value}>
            <div className={classes.headerLeft}>
              <span className={classes.selectedLabel}>
                {t('content.release_note.modal.create.article_current_selected')}
              </span>
              <span className={classes.selectedVal}>{selectedTitle}</span>
            </div>
            <span className={classes.clearText} onClick={handleClearClick}>
              {t('content.release_note.modal.create.article_clear')}
            </span>
          </When>
          <Otherwise>
            <span className={classes.emptyText}>
              {t('content.release_note.modal.create.article_empty_text')}
            </span>
          </Otherwise>
        </Choose>
      </div>
      <Choose>
        <When condition={loading}>
          <Spin />
        </When>
        <Otherwise>
          <div className={classes.sectionList}>
            <div className={classes.section}>
              <h4 className={classes.sectionHeader}>{t('cms.content.article.title.properties')}</h4>
              <Choose>
                <When condition={articles?.property?.length === 0}>
                  <div className={classes.sectionEmpty}>
                    {t('content.release_note.modal.create.article_section_empty')}
                  </div>
                </When>
                <Otherwise>
                  <div className={classes.sectionList}>
                    <For of={articles?.property ?? []} each="article" index="index">
                      <div
                        className={classNames(classes.article, {
                          [classes.selectedArticle]: value === article.id,
                        })}
                        onClick={() => handleSelectArticle(article)}
                      >
                        <div>
                          <span className={classes.articleNum}>{index + 1}</span>
                          <span className={classes.articleTitle}>{article.title}</span>
                        </div>

                        <If condition={value === article.id}>
                          <CheckOutlined />
                        </If>
                      </div>
                    </For>
                  </div>
                </Otherwise>
              </Choose>
            </div>
            <div className={classes.section}>
              <h4 className={classes.sectionHeader}>{t('cms.content.article.title.bookings')}</h4>
              <Choose>
                <When condition={articles?.booking?.length === 0}>
                  <div className={classes.sectionEmpty}>
                    {t('content.release_note.modal.create.article_section_empty')}
                  </div>
                </When>
                <Otherwise>
                  <div className={classes.sectionList}>
                    <For of={articles?.booking ?? []} each="article" index="index">
                      <div
                        className={classNames(classes.article, {
                          [classes.selectedArticle]: value === article.id,
                        })}
                        onClick={() => handleSelectArticle(article)}
                      >
                        <div>
                          <span className={classes.articleNum}>{index + 1}</span>
                          <span className={classes.articleTitle}>{article.title}</span>
                        </div>

                        <If condition={value === article.id}>
                          <CheckOutlined />
                        </If>
                      </div>
                    </For>
                  </div>
                </Otherwise>
              </Choose>
            </div>
            <div className={classes.section}>
              <h4 className={classes.sectionHeader}>{t('cms.content.article.title.payment')}</h4>
              <Choose>
                <When condition={articles?.payment?.length === 0}>
                  <div className={classes.sectionEmpty}>
                    {t('content.release_note.modal.create.article_section_empty')}
                  </div>
                </When>
                <Otherwise>
                  <div className={classes.sectionList}>
                    <For of={articles?.payment ?? []} each="article" index="index">
                      <div
                        className={classNames(classes.article, {
                          [classes.selectedArticle]: value === article.id,
                        })}
                        onClick={() => handleSelectArticle(article)}
                      >
                        <div>
                          <span className={classes.articleNum}>{index + 1}</span>
                          <span className={classes.articleTitle}>{article.title}</span>
                        </div>

                        <If condition={value === article.id}>
                          <CheckOutlined />
                        </If>
                      </div>
                    </For>
                  </div>
                </Otherwise>
              </Choose>
            </div>
          </div>
        </Otherwise>
      </Choose>
    </div>
  );
};

export default Articles;
